import React, { useState, useMemo } from 'react'
import { Modal } from 'react-bootstrap'
import { IGuest, ITable } from '../../../interface/index.ts'
import './index.css'

interface Props {
	show: boolean
	onHide: () => void
	guests: IGuest[]
	table: ITable | null
	seatIndex: number | null
	onSelectGuest: (guest: IGuest) => void
}

const GuestSelectorModal: React.FC<Props> = ({
	show,
	onHide,
	guests,
	table,
	seatIndex,
	onSelectGuest
}) => {
	const [searchQuery, setSearchQuery] = useState('')

	// Filter to only unassigned guests
	const unassignedGuests = useMemo(() => {
		return guests.filter(g => !g.tableId)
	}, [guests])

	// Apply search filter
	const filteredGuests = useMemo(() => {
		if (!searchQuery.trim()) return unassignedGuests

		const query = searchQuery.toLowerCase()
		return unassignedGuests.filter(g =>
			`${g.firstName} ${g.lastName}`.toLowerCase().includes(query)
		)
	}, [unassignedGuests, searchQuery])

	const handleSelectGuest = (guest: IGuest) => {
		onSelectGuest(guest)
		setSearchQuery('')
		onHide()
	}

	const handleClose = () => {
		setSearchQuery('')
		onHide()
	}

	const getStatusIcon = (status: string) => {
		switch (status) {
			case 'confirmed': return '✓'
			case 'declined': return '✗'
			default: return '?'
		}
	}

	const getStatusClass = (status: string) => {
		switch (status) {
			case 'confirmed': return 'status-confirmed'
			case 'declined': return 'status-declined'
			default: return 'status-pending'
		}
	}

	return (
		<Modal
			show={show}
			onHide={handleClose}
			centered
			className='guest-selector-modal'
			size='sm'
		>
			<Modal.Header closeButton>
				<Modal.Title>
					{table ? `${table.name} - Mjesto ${(seatIndex ?? 0) + 1}` : 'Odaberi gosta'}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className='guest-selector-search'>
					<input
						type='text'
						placeholder='Pretraži goste...'
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className='guest-search-input'
						autoFocus
					/>
				</div>

				<div className='guest-selector-list'>
					{filteredGuests.length === 0
						? (
							<div className='no-guests-message'>
								{unassignedGuests.length === 0
									? 'Svi gosti su već raspoređeni'
									: 'Nema rezultata pretrage'}
							</div>
						)
						: (
							filteredGuests.map(guest => (
								<button
									key={guest.id}
									className='guest-selector-item'
									onClick={() => handleSelectGuest(guest)}
								>
									<span className={`guest-status-icon ${getStatusClass(guest.rsvpStatus)}`}>
										{getStatusIcon(guest.rsvpStatus)}
									</span>
									<span className='guest-full-name'>
										{guest.firstName} {guest.lastName}
									</span>
								</button>
							))
						)}
				</div>
			</Modal.Body>
		</Modal>
	)
}

export default GuestSelectorModal
